import React, { ButtonHTMLAttributes } from 'react'
import classNames from 'classnames'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'quaternary'
  | 'destructive'
  | 'link'
  | 'linkDestructive'

export type ButtonSize = 'small' | 'regular'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * ボタンのバリエーション
   * primary: 青色の背景に白いテキスト
   * secondary: 白色の背景に青い枠線と青いテキスト
   * tertiary: 薄いグレー背景に青いテキスト（丸みを帯びたデザイン）
   * quaternary: 白色の背景に青いテキスト
   * destructive: 白色の背景に赤い枠線と赤いテキスト
   * link: テキストのみのリンクボタン（青色）
   * linkDestructive: テキストのみのリンクボタン（赤色）
   */
  variant?: ButtonVariant
  /**
   * ボタンのサイズ
   * small: 小サイズ
   * regular: 通常サイズ
   */
  size?: ButtonSize
  /**
   * ボタンを幅いっぱいに広げる
   */
  fullWidth?: boolean
  /**
   * ボタンを無効化する
   */
  disabled?: boolean
}

/**
 * みてねのデザインシステムに準拠したボタンコンポーネント
 * Figmaデザインに沿った実装
 */
export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'regular',
  fullWidth = false,
  disabled = false,
  className,
  children,
  ...props
}) => {
  // 共通ベーススタイル
  const baseStyles = 'inline-flex items-center justify-center font-semibold transition-colors duration-200 focus:outline-none'

  // ボタン形状・サイズ（disabled状態でも共通）
  const shapeStyles = {
    small: {
      primary: 'rounded-lg min-h-[36px]',
      secondary: 'rounded-lg min-h-[36px]',
      tertiary: 'rounded-[15px]',
      quaternary: 'rounded-lg',
      destructive: 'rounded-lg min-h-[36px]',
      link: 'min-h-[36px]',
      linkDestructive: 'min-h-[36px]',
    },
    regular: {
      primary: 'rounded-lg min-h-[48px]',
      secondary: 'rounded-lg min-h-[48px]',
      tertiary: 'rounded-[20px]',
      quaternary: 'rounded-lg',
      destructive: 'rounded-lg min-h-[48px]',
      link: 'min-h-[36px]',
      linkDestructive: 'min-h-[36px]',
    },
  }

  // サイズスタイル（disabled状態でも共通）
  const sizeStyles = {
    small: {
      tertiary: 'text-sm px-4 py-0 h-7', // tertiaryは常に14px (text-sm)
      link: 'text-fixed-s py-2',
      linkDestructive: 'text-fixed-s py-2',
      primary: 'text-fixed-s px-4 py-2',
      secondary: 'text-fixed-s px-4 py-2',
      quaternary: 'text-fixed-s px-4 py-2',
      destructive: 'text-fixed-s px-4 py-2',
    },
    regular: {
      tertiary: 'text-sm px-4 py-0 h-9', // tertiaryは常に14px (text-sm)
      link: 'text-base py-[11px]',
      linkDestructive: 'text-base py-[11px]',
      primary: 'text-base px-4 py-[11px]',
      secondary: 'text-base px-4 py-[11px]',
      quaternary: 'text-base px-4 py-[11px]',
      destructive: 'text-base px-4 py-[11px]',
    },
  }

  // 通常状態のスタイル（背景・テキスト・枠線）
  const normalStyles = {
    primary: 'bg-mi-bg-primary text-white',
    secondary: 'bg-white text-mi-text-primary border border-mi-border-primary',
    tertiary: 'bg-mi-bg-surface-secondary text-mi-text-primary',
    quaternary: 'bg-white text-mi-text-primary',
    destructive: 'bg-white text-mi-on-surface-destructive border border-mi-on-surface-destructive',
    link: 'bg-transparent text-mi-text-primary',
    linkDestructive: 'bg-transparent text-mi-on-surface-destructive',
  }

  // 無効状態のスタイル（背景・テキスト・枠線）
  const disabledStyles = {
    primary: 'bg-mi-surface-disabled text-white pointer-events-none',
    secondary: 'bg-white text-mi-surface-disabled border border-mi-surface-disabled pointer-events-none',
    tertiary: 'bg-mi-bg-surface-secondary text-mi-surface-disabled pointer-events-none',
    quaternary: 'bg-white text-mi-surface-disabled pointer-events-none',
    destructive: 'bg-white text-mi-surface-disabled border border-mi-surface-disabled pointer-events-none',
    link: 'bg-transparent text-mi-surface-disabled pointer-events-none',
    linkDestructive: 'bg-transparent text-mi-surface-disabled pointer-events-none',
  }

  return (
    <button
      className={classNames(
        baseStyles,
        shapeStyles[size][variant],
        sizeStyles[size][variant],
        disabled ? disabledStyles[variant] : normalStyles[variant],
        fullWidth ? 'w-full' : '',
        className
      )}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  )
}
